/**
 * ⚖️ R71 (CD 2026-08-22) — A REFUSAL IS A LINE, NOT AN EVENT.
 *
 * A `409 replan_blocked_in_flight` renders WHERE THE ASK WAS MADE: the control
 * settles back to rest — no spinner left running, no permanently disabled
 * button — and one quiet line appears beneath it.
 *
 * ⛔ NO SHEET. NO TOAST. NO MODAL. NO `--warn`. NO RED. NO WARNING GLYPH.
 * Nothing about this is an incident, and a minor reads it as one if the chrome
 * says so. (This replaces the `.esError` block the 409 used to fall into,
 * which was `--danger-soft` behind a red border with `role="alert"` — the exact
 * incident chrome the ruling forbids.)
 *
 * ⛔ NO RETRY. A retry button invites the athlete to fight a lock she cannot
 * see; the coach owns the queue.
 *
 * The anatomy is DISC-3's resting line, reused deliberately so the two quiet
 * states of this app look like each other: a 6px disabled-grey dot beside a
 * 12px muted line. The one departure is declared in the CSS — RPL-1 wraps to
 * two lines, so the dot pins to the FIRST line rather than centring.
 *
 * LIFETIME. The line clears on the next successful generate or when the stored
 * proposal arrives, and it NEVER persists across sessions: it is derived from
 * the live error state, never stored, so a reload starts silent.
 */

import { RPL_REFUSED } from '../../lib/seasonPlanCopy';
import styles from './ReplanRefusalLine.module.css';

interface ReplanRefusalLineProps {
  /**
   * B99 — the anatomy is the point, so the OTHER quiet hold on this surface
   * reuses it rather than growing chrome of its own: a COPPA-held athlete gets
   * `GUARDIAN_HOLD_MESSAGE` (lib/planEdit, the line the EditPanel already
   * shows) through this same line. Defaults to RPL-1, so every existing
   * caller renders byte-identical.
   *
   * B130 — an explicit `''` (never `undefined`, so the RPL-1 default is
   * untouched) renders the region with no dot and no text: `:empty` in the
   * module CSS. A caller that mounts this component unconditionally and
   * passes `''` until the hold arrives gets the node into the DOM before the
   * text does, so the fill lands in a region the reader already has rather
   * than as a whole new node arriving with its text already inside — the
   * same reason an inserted node "often does not announce" (B130's filing).
   */
  message?: string;
}

export function ReplanRefusalLine({ message = RPL_REFUSED }: ReplanRefusalLineProps) {
  return (
    // `role="status"` (polite), NEVER `role="alert"`: an assertive live region
    // is the audible twin of the red box this line replaced.
    <p className={styles.line} role="status">
      {message ? (
        <>
          <span className={styles.dot} aria-hidden="true" />
          {message}
        </>
      ) : null}
    </p>
  );
}
